// Read workflow data from the saved test record.
import dayjs from 'dayjs';
import { structureOf } from '../epmHelpers';

// ── Formatting ──────────────────────────────────────────────────────────────
/** 2.4 → "+2.4%", −1.1 → "−1.1%" (typographic minus). */
export const fmtPct = (value, digits = 1) => !Number.isFinite(value) ? '—' : `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toFixed(digits)}%`;

/** Signed number without the % sign, for interval bounds: "+0.9", "−2.0". */
export const fmtSigned = (value, digits = 1) => !Number.isFinite(value) ? '—' : `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(value).toFixed(digits)}`;

/** "+2.4%" / "−1.1%" / "-1.1" → number, or null. */
export const parsePct = (text) => {
    if (typeof text === 'number') return text;
    const match = String(text || '').replace('−', '-').match(/[-+]?\d+(\.\d+)?/);
    return match ? Number(match[0]) : null;
};

export const fmtP = (p) => !Number.isFinite(p) ? '—' : (p < 0.001 ? '< 0.001' : p.toFixed(3));

/** "Inês Carvalho" → "I. Carvalho"; names already short are kept. */
export const shortName = (name) => {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length < 2 || words[0].endsWith('.')) return words.join(' ');
    return `${words[0][0]}. ${words.slice(1).join(' ')}`;
};

export const planOf = (test) => {
    const plan = { ...(structureOf(test).plan || {}), ...(test?.plans || {}) };
    return { ...plan, primary_metric: plan.primary_metric || '', metric: {
        name: plan.primary_metric || '', baseline: plan.baseline ?? null,
        dailyTraffic: plan.dailyTraffic ?? null, ...(plan.metric || {}),
    } };
};

export const scheduleOf = (test) => {
    const today = dayjs().startOf('day');
    const offset = (value) => value && dayjs(value).isValid()
        ? dayjs(value).startOf('day').diff(today, 'day') : null;
    return { lane: test?.schedule?.lane || test?.ideation?.location?.funnel_step || '',
        start_day: offset(test?.schedule?.date_time), end_day: offset(test?.schedule?.end_date_time) };
};

export const progressOf = (test) => {
    const { start_day: start, end_day: end } = scheduleOf(test);
    if (start === null || end === null || end <= start) return null;
    const total = end - start;
    return { day: Math.min(total, Math.max(1, 1 - start)), total };
};

export const parseExperience = (text) => {
    const parts = String(text || '').split('·').map((part) => part.trim()).filter(Boolean);
    const id = parts.find((part) => /^exp_/.test(part)) || null;
    const versionPart = parts.length > 1 ? parts[parts.length - 1] : '';
    const version = Number((versionPart.match(/^v(\d+)$/) || [])[1]) || 1;
    return { name: parts[0] || 'Experience', id, version };
};

export const buildOf = (test, options = {}) => {
    const lists = Array.isArray(options) ? options : options.checklists || [];
    const staging = options.stagingChecklist || lists.filter((c) => c.env?.toUpperCase() === 'STAGING');
    const production = options.productionChecklist || lists.filter((c) => c.env?.toUpperCase() === 'PRODUCTION');
    const checked = new Set((test?.build_qa?.checklist || []).map(String));
    const saved = test?.build_qa || {};
    const summary = (items, audit = {}) => ({
        ...audit, total: items.length,
        done: items.filter((item) => checked.has(String(item._id || item.id))).length,
        signed_by: audit.signed_by || null, signed_day: audit.signed_day ?? null,
    });
    return { ...saved, added: saved.added || [],
        staging: summary(staging, saved.staging), production: summary(production, saved.production),
        exp: parseExperience(saved.experience), lost: Boolean(saved.lost),
    };
};

export const resultOf = (test) => {
    const saved = structureOf(test).result || {};
    return { ...saved, verdict: test?.verdict?.status?.toLowerCase() || saved.verdict || null,
        uplift: parsePct(saved.uplift) ?? NaN, ci: saved.ci || [NaN, NaN],
        p: saved.p ?? NaN, exposures: saved.exposures ?? NaN, rates: saved.rates || [NaN, NaN],
        power: saved.power ?? NaN, srmP: saved.srmP ?? NaN, secondary: saved.secondary || [],
        rationale: test?.verdict?.reason || saved.rationale || '', learning: saved.learning || '',
        next: saved.next || '', settlingNote: saved.settlingNote || '',
    };
};

export const significancePath = (test) => structureOf(test).result?.significance_path || [];

/** The hypothesis as a string, or the description when none was written. */
export const hypothesisText = (test) => {
    return test?.ideation?.hypothesis || test?.description || '—';
};
